import React, { FC } from "react";
import { Check, EllipsisVertical, ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type ChatFilterType = "inbox" | "assigned_to_me" | "resolved" | "all";

const SEGMENT_FILTERS: ChatFilterType[] = ["inbox", "all"];

const FILTER_LABEL_KEY: Record<ChatFilterType, string> = {
  inbox: "chats.inbox",
  assigned_to_me: "chats.assigned",
  resolved: "chats.resolved",
  all: "chats.all",
};

interface ChatFiltersProps {
  activeFilter: ChatFilterType;
  onFilterChange: (filter: ChatFilterType) => void;
  pinnedOnly: boolean;
  onPinnedOnlyChange: (pinnedOnly: boolean) => void;
  className?: string;
}

export const ChatFilters: FC<ChatFiltersProps> = ({
  activeFilter,
  onFilterChange,
  pinnedOnly,
  onPinnedOnlyChange,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex h-[62px] items-center gap-2 border-b border-neutral-200 px-4",
        className,
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("chats.filters")}
            className={cn(
              "flex h-8 shrink-0 items-center gap-1 rounded-lg border px-3 text-xs font-medium shadow-xs transition-colors",
              pinnedOnly
                ? "border-royal-glow-3 bg-royal-glow-5 text-neutral-950"
                : "border-neutral-200 bg-white text-neutral-950 hover:bg-neutral-50",
            )}
          >
            <ListFilter className="size-4" />
            <span>{t("chats.filters")}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52 py-2">
          <DropdownMenuItem
            role="menuitemcheckbox"
            aria-checked={pinnedOnly}
            onSelect={(event) => {
              event.preventDefault();
              onPinnedOnlyChange(!pinnedOnly);
            }}
            className="py-2.5"
          >
            <span>{t("chats.pinnedOnly")}</span>
            {pinnedOnly && <Check className="ml-auto size-4" aria-hidden />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="ml-auto flex h-8 items-center gap-0.5 rounded-[10px] bg-neutral-100 p-[3px]">
        {SEGMENT_FILTERS.map((filter) => {
          const isActive = activeFilter === filter;
          return (
            <button
              key={filter}
              type="button"
              onClick={() => onFilterChange(filter)}
              className={cn(
                "flex h-6 items-center justify-center rounded-lg px-3 text-sm font-medium transition-colors",
                isActive
                  ? "bg-white text-neutral-950 shadow-sm"
                  : "text-neutral-500 hover:text-neutral-950",
              )}
            >
              {t(FILTER_LABEL_KEY[filter])}
            </button>
          );
        })}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("chats.filters")}
              className={cn(
                "flex size-6 items-center justify-center rounded-lg transition-colors",
                activeFilter === "assigned_to_me" || activeFilter === "resolved"
                  ? "bg-white text-neutral-950 shadow-sm"
                  : "text-neutral-500 hover:text-neutral-950",
              )}
            >
              <EllipsisVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuRadioGroup
              value={activeFilter}
              onValueChange={(value) => onFilterChange(value as ChatFilterType)}
            >
              {(Object.keys(FILTER_LABEL_KEY) as ChatFilterType[]).map(
                (filter) => (
                  <DropdownMenuRadioItem key={filter} value={filter}>
                    {t(FILTER_LABEL_KEY[filter])}
                  </DropdownMenuRadioItem>
                ),
              )}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
